'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Button,
  Card,
  EmptyState,
  Skeleton,
  DataTable,
  DataTableHeader,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  DataTableHeaderCell,
  DataTableSortableHeader,
  SearchInput,
  PaginationNav,
  Input,
} from '@/components/ui';
import { FilterBar, selectValue, type FilterGroup, type FilterValue } from './FilterBar';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import { useIsAtLeast } from './responsive-table';
import { useHydrated } from './use-hydrated';
import type { SortDirection } from './DataTable';

export type LoadParams = {
  page: number;
  pageSize: number;
  sortKey: string;
  sortDir: 'asc' | 'desc';
  [filterKey: string]: string | number | undefined;
};

export type ColumnDef<T> = {
  key: string;
  label: string;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  /** Column width: CSS value, e.g. "20%" or "120px". */
  width?: string;
  render: (row: T) => React.ReactNode;
};

export type FilterDefSelect = {
  key: string;
  label: string;
  type: 'select';
  allLabel: string;
  /** Available values are authoritative; selections absent from this list are cleared. */
  options: Array<{ value: string; label: string }>;
};

export type FilterDefSearch = {
  key: string;
  label: string;
  type: 'search';
  placeholder?: string;
};

export type FilterDefNumber = {
  key: string;
  label: string;
  type: 'number';
  placeholder?: string;
  min?: number;
  max?: number;
};

export type FilterDefRange = {
  /** Unique key for this filter (e.g. 'ratingRange'); min/max values use minKey and maxKey. */
  key: string;
  label: string;
  type: 'range';
  /** Filter key for minimum value passed to loadData (e.g. 'ratingMin'). */
  minKey: string;
  /** Filter key for maximum value passed to loadData (e.g. 'ratingMax'). */
  maxKey: string;
  min: number;
  max: number;
  /** @deprecated Unused since the toolbar moved onto FilterBar (the group label titles the range). */
  rangeTitle?: string;
  /** @deprecated Unused: FilterBar's panel has its own Reset / Clear all. */
  resetLabel?: string;
  /** @deprecated Unused: the range applies live. */
  applyLabel?: string;
  /** Accessible name of the min input (default `Min value`). */
  minLabel?: string;
  /** Accessible name of the max input (default `Max value`). */
  maxLabel?: string;
  /**
   * Optional secondary toggle stored under this key (as 'true' / 'false') and rendered
   * as a checkbox inside the popover. Used for the "Include unrated" affordance on the
   * rating range so NULL ratings are not silently dropped when bounds are applied.
   */
  extraToggleKey?: string;
  extraToggleLabel?: string;
  extraToggleDefault?: boolean;
  /** Applied-chip text when only the toggle differs from its default, e.g. `Excluding unrated`. */
  extraToggleChipLabel?: string;
  /** Short text appended to the range chip when both moved, e.g. `excl. unrated`. */
  extraToggleChipSuffix?: string;
};

export type FilterDef = FilterDefSelect | FilterDefSearch | FilterDefNumber | FilterDefRange;

export type PaginatedDataTableProps<T extends { id: string }> = {
  /** Load data for the current page/filters/sort. Called when params change. */
  loadData: (params: LoadParams) => Promise<{ data: T[]; total: number }>;
  /** Column definitions: key (used for sort), label, optional sortable/align, render. */
  columns: ColumnDef<T>[];
  /**
   * Optional filter definitions, rendered by `FilterBar` (search slot; select / range groups).
   * Filter keys are passed to loadData.
   */
  filters?: FilterDef[];
  /** Default sort column key. */
  defaultSortKey?: string;
  /** Default sort direction. */
  defaultSortDir?: SortDirection;
  /** Page size options. */
  pageSizeOptions?: ReadonlyArray<number>;
  /** Default page size (number). */
  defaultPageSize?: number;
  /** Debounce delay for search filter in ms. */
  searchDebounceMs?: number;
  /** Title shown above the table (e.g. "Venues"). */
  title?: string;
  /** Optional subtitle shown under title, e.g. "LOCATIONS" → displays "{total} LOCATIONS" in small caps. */
  subtitle?: string;
  /** Optional content to show next to the title (e.g. Add button). */
  headerActions?: React.ReactNode;
  /** Optional actions column: render per row (e.g. Edit/Delete buttons). */
  renderActions?: (row: T) => React.ReactNode;
  /** Optional width for the actions column, e.g. "180px". */
  actionsColumnWidth?: string;
  /** Shown when loadData fails. */
  errorMessage?: string;
  /** Retry label for error state. */
  retryLabel?: string;
  /** When this value changes, data is refetched (e.g. increment after delete). */
  refreshTrigger?: number;
  /** Initial values for filters (e.g. { timePeriod: 'upcoming_current' }). */
  defaultFilterValues?: Record<string, string>;
  /** Optional custom content when there are no rows (e.g. CTA to create first item). */
  emptyContent?: React.ReactNode;
  /** Optional mobile card renderer. When provided, shows cards on mobile instead of the table. */
  renderMobileCard?: (row: T) => React.ReactNode;
  /**
   * Layout variant. `'table'` (default) is the full table with card chrome.
   * `'rail'` is a compact, always-card vertical list for a master-detail left
   * rail: the same search / filter / sort / pagination engine, rendered narrow.
   * Requires `renderRailItem`; the page owns the heading in this mode.
   */
  variant?: 'table' | 'rail';
  /**
   * Rail mode only: render one compact row per record. Receives the row plus
   * whether it is the currently-selected row (for active highlighting).
   */
  renderRailItem?: (row: T, opts: { selected: boolean }) => React.ReactNode;
  /** Rail mode only: id of the currently-selected row. */
  selectedId?: string | null;
  /** Rail mode only: called when a row is clicked or activated via keyboard. */
  onRowSelect?: (row: T) => void;
};

type FilterState = Record<string, string>;

/** The FilterBar groups for the table's select and range defs, in `filters` order. */
function toFilterGroups(filters: FilterDef[]): FilterGroup[] {
  return filters.flatMap<FilterGroup>((f) => {
    if (f.type === 'select') {
      return [
        { kind: 'select', id: f.key, label: f.label, allLabel: f.allLabel, options: f.options },
      ];
    }
    if (f.type === 'range') {
      const toggleLabel = f.extraToggleLabel;
      return [
        {
          kind: 'range',
          id: f.key,
          label: f.label,
          min: f.min,
          max: f.max,
          minLabel: f.minLabel ?? 'Min value',
          maxLabel: f.maxLabel ?? 'Max value',
          toggle:
            f.extraToggleKey && toggleLabel
              ? {
                  label: toggleLabel,
                  default: f.extraToggleDefault ?? true,
                  // The chip only shows while the toggle differs from its default, so the
                  // text does not depend on the state.
                  chipLabel: f.extraToggleChipLabel ? () => f.extraToggleChipLabel! : undefined,
                  chipSuffix: f.extraToggleChipSuffix ? () => f.extraToggleChipSuffix! : undefined,
                }
              : undefined,
        },
      ];
    }
    return [];
  });
}

/** Table filter state (flat strings, as sent to `loadData`) as the FilterBar's value. */
function toFilterValue(filters: FilterDef[], state: FilterState): FilterValue {
  const out: FilterValue = {};
  for (const f of filters) {
    if (f.type === 'select') {
      out[f.key] = state[f.key] || undefined;
    } else if (f.type === 'range') {
      out[f.key] = {
        min: Number(state[f.minKey]),
        max: Number(state[f.maxKey]),
        toggle: f.extraToggleKey ? state[f.extraToggleKey] === 'true' : undefined,
      };
    }
  }
  return out;
}

/** Writes the FilterBar's next value back into the flat state; returns the same object if equal. */
function applyFilterValue(
  filters: FilterDef[],
  state: FilterState,
  next: FilterValue,
): FilterState {
  const out = { ...state };
  for (const f of filters) {
    if (f.type === 'select') {
      if (!(f.key in next)) continue;
      const v = selectValue(next, f.key);
      out[f.key] = v ?? '';
    } else if (f.type === 'range') {
      if (!(f.key in next)) continue;
      const v = next[f.key];
      const r = typeof v === 'object' && !Array.isArray(v) ? v : undefined;
      out[f.minKey] = String(r?.min ?? f.min);
      out[f.maxKey] = String(r?.max ?? f.max);
      if (f.extraToggleKey) {
        out[f.extraToggleKey] = String(r?.toggle ?? f.extraToggleDefault ?? true);
      }
    }
  }
  return Object.keys(out).every((k) => out[k] === state[k]) ? state : out;
}

/** Drop selections that no longer exist; a remaining valid option still constrains the data. */
function normalizeSelectFilters(filters: FilterDef[], state: FilterState): FilterState {
  let next = state;
  for (const filter of filters) {
    if (
      filter.type === 'select' &&
      state[filter.key] &&
      !filter.options.some((option) => option.value === state[filter.key])
    ) {
      if (next === state) next = { ...state };
      next[filter.key] = '';
    }
  }
  return next;
}

/**
 * Filter toolbar, rendered by `FilterBar` (the design system's one filter component): the
 * `search` defs fill its search slot, `select` / `range` defs are its groups (one select = a
 * dropdown beside the search; otherwise the Filter button, popover from `lg`, sheet below).
 * `number` defs have no callers and keep their old inline input, shown under the bar.
 * With `compact` the lone dropdown stacks under the search at every width (the narrow rail).
 */
function FilterToolbar({
  filters,
  filterValues,
  onFiltersChange,
  setFilterAndResetPage,
  onClearSearch,
  searchResetKey,
  resultCount,
  searchDebounceMs,
  compact = false,
  stickyOnMobile = false,
}: {
  filters: FilterDef[];
  filterValues: FilterState;
  onFiltersChange: (next: FilterValue) => void;
  setFilterAndResetPage: (key: string, value: string) => void;
  /** Empties every search def's applied value and returns to page 1, in one update. */
  onClearSearch: () => void;
  /** Bumps on each clear: remounts the search inputs so a still-debouncing keystroke is dropped. */
  searchResetKey: number;
  resultCount: number;
  searchDebounceMs: number;
  compact?: boolean;
  /** Pin the phone toolbar (search) to the top of the scroll area; used with mobile cards. */
  stickyOnMobile?: boolean;
}) {
  const t = useTranslations('Common.ui.filterBar');
  const groups = toFilterGroups(filters);
  const searches = filters.filter((f): f is FilterDefSearch => f.type === 'search');
  const numbers = filters.filter((f): f is FilterDefNumber => f.type === 'number');

  const search =
    searches.length > 0 ? (
      <div className="flex flex-col gap-2">
        {searches.map((f) => (
          <SearchInput
            key={`${f.key}:${searchResetKey}`}
            placeholder={f.placeholder}
            aria-label={f.label}
            value={filterValues[f.key] ?? ''}
            onChange={(v) => setFilterAndResetPage(f.key, v)}
            debounceMs={searchDebounceMs}
            className="h-11"
          />
        ))}
      </div>
    ) : undefined;

  return (
    <div
      className={cn(
        compact ? 'mb-3' : 'mb-3 md:mb-4',
        stickyOnMobile &&
          'bg-background sticky top-0 z-10 pb-2 md:static md:bg-transparent md:pb-0',
      )}
    >
      <FilterBar
        compact={compact}
        groups={groups}
        value={toFilterValue(filters, filterValues)}
        onChange={onFiltersChange}
        resultCount={resultCount}
        search={search}
        searchActive={searches.some((f) => (filterValues[f.key] ?? '').trim() !== '')}
        onClearSearch={searches.length > 0 ? onClearSearch : undefined}
        labels={{
          filters: t('filters'),
          filtersApplied: (count) => t('filtersApplied', { count }),
          clearAll: t('clearAll'),
          reset: t('reset'),
          show: (count) => t('show', { count }),
          remove: (label) => t('remove', { label }),
        }}
      />
      {numbers.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {numbers.map((f) => (
            <Input
              key={f.key}
              type="number"
              placeholder={f.placeholder}
              value={filterValues[f.key] ?? ''}
              onChange={(e) => setFilterAndResetPage(f.key, e.target.value)}
              className="border-border h-11 w-24 rounded-lg"
              aria-label={f.label}
              min={f.min}
              max={f.max}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Rail initial-load placeholder: a few rows shaped like the real rail items
 * (avatar + two text lines + trailing badge) instead of a centred spinner, which
 * looks oversized in a narrow master-detail rail.
 */
function RailSkeleton({ rows = 7 }: { rows?: number }) {
  return (
    <ul className="space-y-1" aria-hidden>
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="border-border flex items-center gap-3 rounded-xl border px-3 py-2.5">
          <span className="bg-foreground/10 h-9 w-9 flex-none animate-pulse rounded-full" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <span className="bg-foreground/10 block h-3 w-2/3 animate-pulse rounded" />
            <span className="bg-foreground/10 block h-2.5 w-2/5 animate-pulse rounded" />
          </div>
          <span className="bg-foreground/10 h-5 w-6 flex-none animate-pulse rounded-full" />
        </li>
      ))}
    </ul>
  );
}

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

export function PaginatedDataTable<T extends { id: string }>({
  loadData,
  columns,
  filters = [],
  defaultSortKey,
  defaultSortDir = 'asc',
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  searchDebounceMs = 350,
  title,
  subtitle,
  headerActions,
  renderActions,
  actionsColumnWidth,
  errorMessage = 'Error loading data',
  retryLabel = 'Retry',
  refreshTrigger = 0,
  defaultFilterValues,
  emptyContent,
  renderMobileCard,
  variant = 'table',
  renderRailItem,
  selectedId = null,
  onRowSelect,
}: PaginatedDataTableProps<T>) {
  const sortKeys = columns.filter((c) => c.sortable !== false).map((c) => c.key);
  const firstSortKey = defaultSortKey ?? sortKeys[0] ?? 'id';

  const [data, setData] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState(firstSortKey);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSortDir);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);
  const [filterValues, setFilterValues] = useState<Record<string, string>>(() =>
    filters.reduce<Record<string, string>>((acc, f) => {
      if (f.type === 'range') {
        const next: Record<string, string> = {
          ...acc,
          [f.minKey]: defaultFilterValues?.[f.minKey] ?? String(f.min),
          [f.maxKey]: defaultFilterValues?.[f.maxKey] ?? String(f.max),
        };
        if (f.extraToggleKey) {
          next[f.extraToggleKey] =
            defaultFilterValues?.[f.extraToggleKey] ?? String(f.extraToggleDefault ?? true);
        }
        return next;
      }
      return { ...acc, [f.key]: defaultFilterValues?.[f.key] ?? '' };
    }, {}),
  );
  const normalizedFilters = normalizeSelectFilters(filters, filterValues);
  if (normalizedFilters !== filterValues) {
    // Reconcile before committing effects: a simultaneous refresh must not fetch stale filters
    // on the old page before fetching the normalized selection on page 1.
    setFilterValues(normalizedFilters);
    if (page !== 1) setPage(1);
  }
  const hasLoadedOnce = useRef(false);
  // Sequence of fetchData calls: only the latest one may write rows, errors or loading.
  const requestSeqRef = useRef(0);
  const wide = useIsAtLeast('md');
  const hydrated = useHydrated();
  // Both layouts only for SSR + first paint; after mount render just the live one.
  const showCards = !!renderMobileCard && (!hydrated || !wide);
  const showTableView = !renderMobileCard || !hydrated || wide;

  const fetchData = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    try {
      setLoading(true);
      setError(null);
      const validSortKey = sortKeys.includes(sortKey) ? sortKey : firstSortKey;
      const params: LoadParams = {
        page,
        pageSize: pageSize,
        sortKey: validSortKey,
        sortDir: sortDirection,
        ...filterValues,
      };
      const result = await loadData(params);
      // A newer request (search keystroke, page click, poll) has started: drop this answer.
      if (seq !== requestSeqRef.current) return;
      const rows = result.data ?? [];
      const rowTotal = result.total ?? 0;
      setData(rows);
      setTotal(rowTotal);
      hasLoadedOnce.current = true;
      // The set shrank under us (e.g. a poll after check-ins): go to the last page that has rows.
      if (rows.length === 0 && rowTotal > 0 && page > 1) {
        setPage(Math.max(1, Math.ceil(rowTotal / pageSize)));
      }
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      console.error('PaginatedDataTable load error:', err);
      setError(errorMessage);
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
    // Intentionally omit sortKeys/firstSortKey - they're derived from columns which is
    // often a new array reference each render; refetch only when actual params change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    pageSize,
    sortKey,
    sortDirection,
    filterValues,
    loadData,
    errorMessage,
    refreshTrigger,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const setFilterAndResetPage = useCallback((key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const [searchResetKey, setSearchResetKey] = useState(0);
  // Clear all's search half. The group half (onFiltersChange) runs in the same event, so React
  // batches both into one state update, one fetch and one page reset.
  const onClearSearch = () => {
    setFilterValues((prev) => {
      const out = { ...prev };
      for (const f of filters) if (f.type === 'search') out[f.key] = '';
      return out;
    });
    setSearchResetKey((k) => k + 1);
    setPage(1);
  };

  const onFiltersChange = (next: FilterValue) => {
    const applied = applyFilterValue(filters, filterValues, next);
    // applyFilterValue returns the same object when nothing changed: keep the page then.
    if (applied === filterValues) return;
    setFilterValues(applied);
    setPage(1);
  };

  const handleSort = useCallback(
    (key: string) => {
      if (!sortKeys.includes(key)) return;
      setSortKey(key);
      setSortDirection((d) => (sortKey === key && d === 'asc' ? 'desc' : 'asc'));
      setPage(1);
    },
    [sortKey, sortKeys],
  );

  const isInitialLoad = loading && !hasLoadedOnce.current && !error;

  if (error) {
    return (
      <Card>
        <div className="p-6 text-center">
          <p className="text-danger">{error}</p>
          <Button onClick={fetchData} className="mt-2">
            {retryLabel}
          </Button>
        </div>
      </Card>
    );
  }

  const showTable = total > 0 || loading;

  // ── Rail variant: compact card list for a master-detail left rail ──────────
  // Reuses the same search/filter/sort/pagination engine; the page owns the
  // heading + any header actions, so no card chrome or title here.
  if (variant === 'rail') {
    return (
      <div className="flex h-full min-h-0 flex-col">
        {filters.length > 0 && (
          <FilterToolbar
            compact
            filters={filters}
            filterValues={filterValues}
            onFiltersChange={onFiltersChange}
            setFilterAndResetPage={setFilterAndResetPage}
            onClearSearch={onClearSearch}
            searchResetKey={searchResetKey}
            resultCount={total}
            searchDebounceMs={searchDebounceMs}
          />
        )}

        {isInitialLoad ? (
          <RailSkeleton />
        ) : total === 0 && !loading ? (
          (emptyContent ?? <EmptyState />)
        ) : showTable ? (
          <>
            <div className="relative min-h-0 flex-1 overflow-y-auto">
              {loading && data.length > 0 && (
                <div
                  className="bg-background/60 absolute inset-0 z-10 flex items-start justify-center rounded-lg pt-8"
                  aria-hidden
                >
                  <span className="text-muted text-sm">Updating…</span>
                </div>
              )}
              <ul className="space-y-1">
                {data.map((row, index) => (
                  <li key={`${row.id}-${index}`}>
                    <div
                      role="button"
                      tabIndex={0}
                      aria-pressed={row.id === selectedId}
                      onClick={() => onRowSelect?.(row)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowSelect?.(row);
                        }
                      }}
                      className="focus-visible:ring-primary/40 rounded-xl outline-none focus-visible:ring-2"
                    >
                      {renderRailItem?.(row, { selected: row.id === selectedId })}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="shrink-0">
              <PaginationNav
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                pageSizeOptions={[...pageSizeOptions]}
                showPageSizeSelector={false}
                compact
              />
            </div>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className="md:bg-card md:border-border md:rounded-xl md:border md:p-6">
      {/* Header: title + subtitle + action */}
      <div className="mb-3 md:mb-6">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            {title != null && (
              <h1 className="text-foreground truncate text-lg font-bold tracking-tight md:text-2xl">
                {title}
              </h1>
            )}
            {subtitle != null && !isInitialLoad && (
              <p className="text-muted mt-0.5 text-xs font-medium tracking-wider uppercase">
                {total} {subtitle}
              </p>
            )}
          </div>
          {headerActions && <div className="shrink-0">{headerActions}</div>}
        </div>
        <div className="border-border mt-4 hidden border-b md:block" aria-hidden />
      </div>

      {/* Toolbar: search + filters. Non-search filters collapse on mobile. */}
      {filters.length > 0 && (
        <FilterToolbar
          filters={filters}
          filterValues={filterValues}
          onFiltersChange={onFiltersChange}
          setFilterAndResetPage={setFilterAndResetPage}
          onClearSearch={onClearSearch}
          searchResetKey={searchResetKey}
          resultCount={total}
          searchDebounceMs={searchDebounceMs}
          stickyOnMobile={!!renderMobileCard}
        />
      )}

      {isInitialLoad ? (
        // First load keeps the title and filters on screen and shows row-shaped placeholders in
        // the body, instead of swapping the whole table for a centred spinner.
        <div aria-busy="true" data-testid="table-skeleton">
          {renderMobileCard && (
            <div className="space-y-3 md:hidden">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton.Card key={i} lines={2} />
              ))}
            </div>
          )}
          <div
            className={
              renderMobileCard
                ? 'relative hidden overflow-x-auto md:block'
                : 'relative overflow-x-auto'
            }
          >
            <DataTable className="border-border min-w-[640px]">
              <DataTableHeader>
                <tr>
                  {columns.map((col) => (
                    <DataTableHeaderCell key={col.key}>{col.label}</DataTableHeaderCell>
                  ))}
                  {renderActions && <DataTableHeaderCell>{''}</DataTableHeaderCell>}
                </tr>
              </DataTableHeader>
              <DataTableBody>
                <Skeleton.TableRows cols={columns.length + (renderActions ? 1 : 0)} rows={6} />
              </DataTableBody>
            </DataTable>
          </div>
        </div>
      ) : total === 0 && !loading ? (
        (emptyContent ?? <EmptyState />)
      ) : showTable ? (
        <>
          <div className="relative">
            {loading && data.length > 0 && (
              <div
                className="bg-background/60 absolute inset-0 z-10 flex items-start justify-center rounded-lg pt-8"
                aria-hidden
              >
                <span className="text-muted text-sm">Updating…</span>
              </div>
            )}

            {/* Mobile card view */}
            {showCards && renderMobileCard && (
              <ul
                role="list"
                className="space-y-3 md:hidden"
                aria-hidden={wide || undefined}
                inert={wide}
              >
                {data.map((row, index) => (
                  <li key={`${row.id}-${index}`}>{renderMobileCard(row)}</li>
                ))}
              </ul>
            )}

            {/* Desktop table view (always shown if no mobile card, otherwise hidden on mobile) */}
            {showTableView && (
              <div
                className={`relative overflow-x-auto ${renderMobileCard ? 'hidden md:block' : ''}`}
                aria-hidden={renderMobileCard && !wide ? true : undefined}
                inert={renderMobileCard ? !wide : undefined}
              >
                <DataTable className="border-border min-w-[640px]">
                  <colgroup>
                    {columns.map((col) => (
                      <col key={col.key} style={col.width ? { width: col.width } : undefined} />
                    ))}
                    {renderActions && (
                      <col style={actionsColumnWidth ? { width: actionsColumnWidth } : undefined} />
                    )}
                  </colgroup>
                  <DataTableHeader>
                    <tr>
                      {columns.map((col) =>
                        col.sortable !== false && sortKeys.includes(col.key) ? (
                          <DataTableSortableHeader
                            key={col.key}
                            sortKey={col.key}
                            currentSortKey={sortKey}
                            direction={sortDirection}
                            onSort={handleSort}
                            align={col.align}
                          >
                            {col.label}
                          </DataTableSortableHeader>
                        ) : (
                          <DataTableHeaderCell key={col.key}>{col.label}</DataTableHeaderCell>
                        ),
                      )}
                      {renderActions && <DataTableHeaderCell>{''}</DataTableHeaderCell>}
                    </tr>
                  </DataTableHeader>
                  <DataTableBody>
                    {data.map((row, index) => (
                      <DataTableRow key={`${row.id}-${index}`}>
                        {columns.map((col) => (
                          <DataTableCell
                            key={col.key}
                            className={
                              col.align === 'right'
                                ? 'text-right'
                                : col.align === 'center'
                                  ? 'text-center'
                                  : undefined
                            }
                          >
                            {col.render(row)}
                          </DataTableCell>
                        ))}
                        {renderActions && (
                          <DataTableCell>
                            <div className="flex items-center justify-end gap-2">
                              {renderActions(row)}
                            </div>
                          </DataTableCell>
                        )}
                      </DataTableRow>
                    ))}
                  </DataTableBody>
                </DataTable>
              </div>
            )}
          </div>
          <div className="mt-4">
            <PaginationNav
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[...pageSizeOptions]}
              showPageSizeSelector
            />
          </div>
        </>
      ) : null}
    </div>
  );
}
